/** A resized image and its pixel dimensions. */
export interface JcResizedImage {
    readonly blob: Blob;
    readonly width: number;
    readonly height: number;
}

export interface JcResizeImageOptions {
    /** The longest edge of the output, in pixels; smaller images are never upscaled. */
    readonly maxEdge: number;
    /** Preferred output MIME type (default `image/webp`); falls back to JPEG where unsupported — check `blob.type`. */
    readonly type?: string;
    /** Encoder quality, 0–1. */
    readonly quality?: number;
}

export class JcImageUtils {
    /** Scales `width`×`height` so its longest edge is at most `maxEdge`, preserving aspect ratio. */
    public static fitWithin(width: number, height: number, maxEdge: number): { width: number; height: number } {
        const scale = Math.min(1, maxEdge / Math.max(width, height));
        return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
    }

    /**
     * Resizes an image file in the browser, preserving its aspect ratio, so an
     * upload is a small re-encoded file (which also drops EXIF/GPS metadata).
     */
    public static async resizeImage(file: File, options: JcResizeImageOptions): Promise<JcResizedImage> {
        const { maxEdge, type = 'image/webp', quality = 0.85 } = options;
        return this.drawToBlob(file, type, quality, image => {
            const { width, height } = this.fitWithin(image.naturalWidth, image.naturalHeight, maxEdge);
            return { width, height, draw: context => context.drawImage(image, 0, 0, width, height) };
        });
    }

    private static loadImage(src: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('Failed to load image'));
            image.src = src;
        });
    }

    /**
     * Resizes an image file to a centered square of `size`x`size` px and returns it
     * as a JPEG blob. The image is center-cropped "cover" style — the shorter side
     * fills the square and the overflow is trimmed, matching how square thumbnails are
     * displayed. EXIF orientation is honored by the browser's default
     * `image-orientation: from-image` handling when the `<img>` is rendered, so
     * phone photos aren't rotated.
     *
     * Runs entirely in the browser, so an upload is a single small file with
     * no server-side processing.
     *
     * @param file The selected image file
     * @param size The target square edge length in pixels
     * @param quality JPEG quality, 0–1
     * @returns A JPEG blob of the cropped, resized image
     */
    public static async resizeImageToSquareJpeg(
        file: File,
        size: number,
        quality = 0.85,
    ): Promise<Blob> {
        const { blob } = await this.drawToBlob(file, 'image/jpeg', quality, image => {
            // "cover": scale so the shorter side fills the square, then center the
            // overflow so the crop is taken from the middle of the image.
            const scale = Math.max(size / image.naturalWidth, size / image.naturalHeight);
            const drawWidth = image.naturalWidth * scale;
            const drawHeight = image.naturalHeight * scale;
            return {
                width: size,
                height: size,
                draw: context => context.drawImage(image, (size - drawWidth) / 2, (size - drawHeight) / 2, drawWidth, drawHeight),
            };
        });
        return blob;
    }

    /** Loads `file`, draws it onto a canvas sized by `layout`, and encodes the canvas. */
    private static async drawToBlob(
        file: File,
        type: string,
        quality: number,
        layout: (image: HTMLImageElement) => { width: number; height: number; draw: (context: CanvasRenderingContext2D) => void },
    ): Promise<JcResizedImage> {
        const objectUrl = URL.createObjectURL(file);
        try {
            const image = await this.loadImage(objectUrl);
            const { width, height, draw } = layout(image);

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext('2d');
            if (context === null) {
                throw new Error('Canvas 2D context is unavailable');
            }
            draw(context);

            const encode = (encodeType: string) => new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                    result => (result !== null ? resolve(result) : reject(new Error('Failed to encode image'))),
                    encodeType,
                    quality,
                );
            });
            // Browsers without an encoder for `type` (e.g. Safari and WebP) silently emit PNG.
            const encoded = await encode(type);
            const blob = encoded.type === type ? encoded : await encode('image/jpeg');
            return { blob, width, height };
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    }
}
